import type {
  AccountingContinuity,
  AccountingExpUpdate,
  ExpExactEvidence,
  ExpAdmission,
  GameEvent,
  GameStateSnapshot,
  TrustedLiveRateMetric,
  ExpPoint as AccountingExpPoint,
  ValidatedObservation,
} from '@/domain/types'
import { enrichExpRequirement, expMatchesRequirement, inferExpRequirement } from './exp-requirement-estimator'

const HOUR_MS = 3_600_000
const MAX_CONTINUOUS_FRAME_GAP_MS = 15_000
const EXP_EVIDENCE_WINDOW_MS = 15_000
const TIMELINE_RETENTION_MS = 180_000
const PROVISIONAL_UNCHANGED_TOLERANCE = 0.0005

interface ExpPoint extends AccountingExpPoint {
  readonly sourceAt: number
}

interface TimelinePoint extends ExpPoint {
  readonly activeAt: number
  readonly segmentId: number
}

interface FrameLocation {
  readonly sourceAt: number
  readonly activeAt: number
  readonly segmentId: number
}

interface PendingLevelTransition {
  readonly from: number
  readonly to: number
  readonly confirmedAt: number
}

interface PendingWrapEvidence {
  readonly evidence: ExpExactEvidence
  readonly activeAt: number
}

export interface TrustedExpAggregate {
  readonly gained: number | null
  readonly trustedDurationMs: number
  readonly perHour: number | null
}

export class TrustedExpLedger {
  private minimumSourceAt = 0
  private suspended = false
  private segmentId = 0
  private segmentOpen = false
  private lastFrameAt: number | null = null
  private frameTimeline: FrameLocation[] = []
  private timeline: TimelinePoint[] = []
  private exactAnchor: TimelinePoint | null = null
  private latestExactEndpoint: ExpPoint | null = null
  private provisionalCandidate: TimelinePoint | null = null
  private provisionalEndpoint: TimelinePoint | null = null
  private pendingLevelTransition: PendingLevelTransition | null = null
  private pendingWrapEvidence: PendingWrapEvidence | null = null
  private readonly requirements = new Map<number, { requiredExp: number; approximate: boolean }>()
  private gained = 0
  private trustedDurationMs = 0
  private hasExactBaseline = false
  private updatedAt: number | null = null
  private admissionEpoch = 0

  reset(at: number, initialState: GameStateSnapshot | null): void {
    this.minimumSourceAt = at
    this.suspended = false
    this.segmentId = 0
    this.segmentOpen = false
    this.lastFrameAt = null
    this.frameTimeline = []
    this.timeline = []
    this.exactAnchor = null
    this.latestExactEndpoint = null
    this.provisionalCandidate = null
    this.provisionalEndpoint = null
    this.pendingLevelTransition = null
    this.pendingWrapEvidence = null
    this.requirements.clear()
    this.gained = 0
    this.trustedDurationMs = 0
    this.hasExactBaseline = false
    this.updatedAt = null
    this.admissionEpoch = 0

    const initial = this.pointFromState(initialState)
    if (!initial) return
    this.rememberRequirement(initial)
    const point: TimelinePoint = { ...initial, activeAt: 0, segmentId: this.segmentId, admittedTotal: 0, admissionEpoch: 0 }
    this.segmentOpen = true
    this.lastFrameAt = initial.sourceAt
    this.frameTimeline = [{ sourceAt: initial.sourceAt, activeAt: 0, segmentId: this.segmentId }]
    this.timeline = [point]
    this.exactAnchor = point
    this.latestExactEndpoint = initial
    this.provisionalEndpoint = point
    this.hasExactBaseline = true
    this.updatedAt = initial.sourceAt
  }

  suspend(): void {
    this.suspended = true
    this.breakContinuity()
  }

  resume(at: number): void {
    this.suspended = false
    this.minimumSourceAt = at
    this.breakContinuity()
  }

  changeGeneration(at: number): void {
    this.minimumSourceAt = at
    this.breakContinuity()
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingExpUpdate {
    if (this.suspended) return this.emptyUpdate('ignored')
    for (const admission of [observation?.expCandidateAdmission, observation?.expEvidenceAdmission]) {
      if (admission?.accounting) this.admissionEpoch = Math.max(this.admissionEpoch, admission.accounting.epoch)
    }
    this.expirePendingEvidence(state.capturedAt)
    const frameContinuity = this.observeCurrentFrame(state, observation, activeAt)
    this.rememberLevelTransition(state, events)
    const carriedUpgrade = this.promotePendingWrap(state)
    if (carriedUpgrade) return this.commitExactEvidence(carriedUpgrade, activeAt)

    const evidence = observation?.expExactEvidence
      ?? (observation === undefined ? this.evidenceFromState(state) : undefined)
    if (!evidence) return this.emptyUpdate(frameContinuity, this.lastFrameAt)

    if (evidence.sourceAt < this.minimumSourceAt) return this.emptyUpdate('ignored', evidence.sourceAt)
    const accepted = observation === undefined || observation.expExactEvidenceAccepted === true
    if (!accepted) {
      if (!observation?.expQuarantined) this.rememberPotentialWrap(evidence, activeAt)
      return this.emptyUpdate('pending', evidence.sourceAt)
    }
    return this.commitExactEvidence(
      evidence,
      activeAt,
      observation?.expExactRebaseline === true || observation?.expRebaseline === true,
      observation?.expEvidenceAdmission?.rebaseline,
      observation?.expEvidenceAdmission,
    )
  }

  aggregate(): TrustedExpAggregate {
    return {
      gained: this.hasExactBaseline ? this.gained : null,
      trustedDurationMs: this.trustedDurationMs,
      perHour: this.hasExactBaseline ? this.rate(this.gained, this.trustedDurationMs) : null,
    }
  }

  liveRate(): TrustedLiveRateMetric {
    if (!this.hasExactBaseline) {
      return {
        value: null,
        status: 'unavailable',
        trustedDurationMs: this.trustedDurationMs,
        updatedAt: this.updatedAt,
      }
    }

    const provisional = this.provisionalContribution()
    const durationMs = this.trustedDurationMs + (provisional?.durationMs ?? 0)
    const total = this.gained + (provisional?.delta ?? 0)
    return {
      value: this.rate(total, durationMs),
      status: provisional ? 'provisional' : 'ready',
      trustedDurationMs: durationMs,
      updatedAt: provisional?.sourceAt ?? this.updatedAt,
    }
  }

  private observeCurrentFrame(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingContinuity {
    const reading = observation?.exp
    const candidate = observation?.expExactCandidate
    const ratio = reading?.availability === 'available'
      ? reading.value?.ratio ?? null
      : observation === undefined
        ? state.exp.ratio
        : candidate?.value.ratio ?? null
    const level = state.level ?? (typeof observation?.level.value === 'number' ? observation.level.value : null)
    if (ratio === null || level === null || !Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
      if (reading?.availability === 'unavailable' && !this.pendingWrapEvidence) this.breakContinuity()
      return reading?.availability === 'unavailable' && !this.pendingWrapEvidence ? 'unavailable' : 'pending'
    }

    const sourceAt = observation
      ? reading!.availability === 'available' ? reading!.capturedAt : candidate?.sourceAt ?? reading!.capturedAt
      : state.observedAt?.exp ?? state.capturedAt
    let continuity: AccountingContinuity = 'contiguous'
    if (!this.segmentOpen || this.lastFrameAt === null) {
      this.openSegment()
      continuity = 'baseline'
    } else if (sourceAt <= this.lastFrameAt) {
      return 'ignored'
    } else if (sourceAt - this.lastFrameAt > MAX_CONTINUOUS_FRAME_GAP_MS) {
      this.breakContinuity()
      this.openSegment()
      continuity = 'discontinuous'
    }
    this.lastFrameAt = sourceAt
    this.frameTimeline.push({ sourceAt, activeAt, segmentId: this.segmentId })
    this.pruneTimeline(sourceAt)

    // A candidate can replace the baseline before exact evidence reaches this ledger.
    if (observation?.expQuarantined || this.admissionEpoch !== (this.exactAnchor?.admissionEpoch ?? 0)) {
      this.provisionalCandidate = null
      this.provisionalEndpoint = null
      return continuity
    }

    const known = this.requirements.get(level)
    const required = state.exp.requiredExp ?? known?.requiredExp ?? null
    if (required === null || required <= 0) return continuity
    const point: TimelinePoint = {
      level,
      absolute: Math.round(required * ratio),
      ratio,
      requiredExp: required,
      requiredExpApproximate: state.exp.requiredExpApproximate ?? known?.approximate ?? true,
      sourceAt,
      activeAt,
      segmentId: this.segmentId,
    }
    this.timeline.push(point)
    if (observation === undefined || observation.accepted.exp) this.observeProvisional(point)
    return continuity
  }

  private observeProvisional(point: TimelinePoint): void {
    const anchor = this.exactAnchor
    if (!anchor || point.segmentId !== anchor.segmentId || point.sourceAt <= anchor.sourceAt) return
    const current = this.provisionalEndpoint
    if (current
      && current.segmentId === point.segmentId
      && current.level === point.level
      && Math.abs(point.ratio - current.ratio) <= PROVISIONAL_UNCHANGED_TOLERANCE) {
      this.provisionalEndpoint = point
      this.provisionalCandidate = null
      return
    }

    const pending = this.provisionalCandidate
    if (pending && this.provisionalSamplesAreCompatible(pending, point)) {
      this.provisionalEndpoint = point
      this.provisionalCandidate = null
      return
    }
    this.provisionalCandidate = point
  }

  private provisionalSamplesAreCompatible(first: TimelinePoint, second: TimelinePoint): boolean {
    if (second.segmentId !== first.segmentId
      || second.sourceAt <= first.sourceAt
      || second.sourceAt - first.sourceAt > MAX_CONTINUOUS_FRAME_GAP_MS
      || second.level < first.level) {
      return false
    }
    if (second.level === first.level) return second.absolute >= first.absolute
    return this.expDifference(first, second) !== null
  }

  private provisionalContribution(): { delta: number; durationMs: number; sourceAt: number } | null {
    const anchor = this.exactAnchor
    const endpoint = this.provisionalEndpoint
    if (!anchor
      || !endpoint
      || endpoint.segmentId !== anchor.segmentId
      || endpoint.sourceAt <= anchor.sourceAt
      || endpoint.activeAt <= anchor.activeAt) {
      return null
    }
    const delta = this.expDifference(anchor, endpoint)
    if (delta === null || delta < 0) return null
    return {
      delta,
      durationMs: endpoint.activeAt - anchor.activeAt,
      sourceAt: endpoint.sourceAt,
    }
  }

  private commitExactEvidence(
    evidence: ExpExactEvidence,
    deliveredActiveAt: number,
    forceRebaseline = false,
    replacementBaseline?: ExpExactEvidence,
    admission?: ExpAdmission,
  ): AccountingExpUpdate {
    let point = this.pointFromEvidence(evidence)
    if (!point) return this.emptyUpdate('ignored', evidence.sourceAt)
    point = this.promotePointWithLevelTransition(point, evidence.deliveredAt)
    if (admission?.accounting) point = { ...point, admittedTotal: admission.accounting.total, admissionEpoch: admission.accounting.epoch }

    const latest = this.latestExactEndpoint
    if (latest && point.sourceAt <= latest.sourceAt) return this.emptyUpdate('ignored', point.sourceAt)
    if (latest && point.level < latest.level && !admission?.accounting) return this.emptyUpdate('ignored', point.sourceAt)
    const epochChanged = admission?.accounting !== undefined && admission.accounting.epoch !== (this.exactAnchor?.admissionEpoch ?? 0)

    const located = this.frameTimeline.find((candidate) => candidate.sourceAt === point.sourceAt)
    const timelinePoint: TimelinePoint = {
      ...point,
      activeAt: located?.activeAt ?? deliveredActiveAt,
      segmentId: located?.segmentId ?? this.segmentId,
    }
    const sameLevelDecrease = latest !== null
      && point.level === latest.level
      && point.absolute < latest.absolute
    if (sameLevelDecrease && !forceRebaseline && !epochChanged) {
      this.rememberPotentialWrap(evidence, deliveredActiveAt)
      return this.emptyUpdate('pending', point.sourceAt)
    }

    this.latestExactEndpoint = point
    this.rememberRequirement(point)
    this.pendingWrapEvidence = null
    this.updatedAt = point.sourceAt
    this.hasExactBaseline = true

    if (admission?.accounting && (epochChanged || forceRebaseline || sameLevelDecrease)) {
      const mark = admission.accounting
      const firstFrame = this.frameTimeline.find(frame => frame.sourceAt === mark.epochStartAt && frame.segmentId === timelinePoint.segmentId)
      const delta = firstFrame ? Math.max(0, mark.total - mark.epochStartTotal) : 0
      const intervalMs = firstFrame ? Math.max(0, timelinePoint.activeAt - firstFrame.activeAt) : 0
      this.gained += delta
      this.trustedDurationMs += intervalMs
      const start = firstFrame ? { ...timelinePoint, sourceAt: firstFrame.sourceAt, activeAt: firstFrame.activeAt,
        admittedTotal: mark.epochStartTotal } : null
      this.rebaseTimelineAt(timelinePoint)
      return this.exactUpdate('rebaseline', start, timelinePoint, delta, intervalMs)
    }

    if (sameLevelDecrease || forceRebaseline || (replacementBaseline && (!latest || latest.sourceAt < replacementBaseline.sourceAt))) {
      if (replacementBaseline && replacementBaseline.level === point.level) {
        const firstFrame = this.frameTimeline.find(frame => frame.sourceAt === replacementBaseline.sourceAt)
        if (!firstFrame || firstFrame.segmentId !== timelinePoint.segmentId) {
          this.rebaseTimelineAt(timelinePoint)
          return this.exactUpdate('rebaseline', null, timelinePoint, null, 0)
        }
        const delta = Math.max(0, point.absolute - replacementBaseline.value.absolute!)
        const start: TimelinePoint = { ...timelinePoint, absolute: replacementBaseline.value.absolute!,
          ratio: replacementBaseline.value.ratio!, sourceAt: replacementBaseline.sourceAt,
          activeAt: firstFrame?.activeAt ?? timelinePoint.activeAt }
        const intervalMs = Math.max(0, timelinePoint.activeAt - start.activeAt)
        this.gained += delta
        this.trustedDurationMs += intervalMs
        this.rebaseTimelineAt(timelinePoint)
        return this.exactUpdate('rebaseline', start, timelinePoint, delta, intervalMs)
      }
      this.rebaseTimelineAt(timelinePoint)
      return this.exactUpdate('rebaseline', null, timelinePoint, null, 0)
    }

    const anchor = this.exactAnchor
    if (!anchor || !located || anchor.segmentId !== timelinePoint.segmentId) {
      this.adoptExactAnchor(timelinePoint)
      return this.exactUpdate(anchor ? 'discontinuous' : 'baseline', null, timelinePoint, null, 0)
    }

    const delta = timelinePoint.admittedTotal !== undefined && anchor.admittedTotal !== undefined
      ? timelinePoint.admittedTotal - anchor.admittedTotal : this.expDifference(anchor, timelinePoint)
    const intervalMs = timelinePoint.activeAt - anchor.activeAt
    if (delta === null || delta < 0 || intervalMs < 0) {
      this.rebaseTimelineAt(timelinePoint)
      return this.exactUpdate('discontinuous', null, timelinePoint, null, 0)
    }

    this.gained += delta
    this.trustedDurationMs += intervalMs
    this.adoptExactAnchor(timelinePoint)
    return this.exactUpdate('contiguous', anchor, timelinePoint, delta, intervalMs)
  }

  private adoptExactAnchor(point: TimelinePoint): void {
    const provisional = this.provisionalEndpoint
    this.exactAnchor = point
    const keepProvisional = provisional !== null
      && provisional.segmentId === point.segmentId
      && provisional.sourceAt > point.sourceAt
      && (this.expDifference(point, provisional) ?? -1) >= 0
    this.provisionalEndpoint = keepProvisional ? provisional : point
    if (this.provisionalCandidate && this.provisionalCandidate.sourceAt <= point.sourceAt) {
      this.provisionalCandidate = null
    }
  }

  private rememberPotentialWrap(evidence: ExpExactEvidence, activeAt: number): void {
    const point = this.rawPointFromEvidence(evidence)
    const anchor = this.latestExactEndpoint
    if (!point
      || !anchor
      || point.level !== anchor.level
      || point.absolute >= anchor.absolute
      || point.sourceAt <= anchor.sourceAt) {
      return
    }
    this.pendingWrapEvidence = { evidence, activeAt }
  }

  private rememberLevelTransition(state: GameStateSnapshot, events: readonly GameEvent[]): void {
    const event = events.find((candidate) => candidate.type === 'level-up'
      && candidate.from !== undefined
      && candidate.to === candidate.from + 1)
    if (event?.from !== undefined && event.to !== undefined) {
      this.pendingLevelTransition = { from: event.from, to: event.to, confirmedAt: event.capturedAt }
      return
    }

    const wrap = this.pendingWrapEvidence
    if (!wrap || wrap.evidence.level === null || state.level !== wrap.evidence.level + 1) return
    this.pendingLevelTransition = {
      from: wrap.evidence.level,
      to: state.level,
      confirmedAt: state.capturedAt,
    }
  }

  private promotePendingWrap(state: GameStateSnapshot): ExpExactEvidence | null {
    const pending = this.pendingWrapEvidence
    if (!pending || pending.evidence.level === null) return null
    const transition = this.pendingLevelTransition
    const targetLevel = transition?.from === pending.evidence.level
      && transition.to === pending.evidence.level + 1
      && Math.abs(transition.confirmedAt - pending.evidence.sourceAt) <= EXP_EVIDENCE_WINDOW_MS
      ? transition.to
      : state.level === pending.evidence.level + 1
        && Math.abs(state.capturedAt - pending.evidence.sourceAt) <= EXP_EVIDENCE_WINDOW_MS
        ? state.level
        : null
    if (targetLevel === null) return null
    const zeroEndpoint = pending.evidence.value.absolute === 0
      && pending.evidence.value.ratio === 0
    if (!zeroEndpoint) return null
    const promoted = {
      ...pending.evidence,
      level: targetLevel,
      deliveredAt: state.capturedAt,
      value: enrichExpRequirement(pending.evidence.value, null, false),
    }
    if (!this.pointFromEvidence(promoted)) return null
    this.pendingWrapEvidence = null
    this.pendingLevelTransition = null
    return promoted
  }

  private promotePointWithLevelTransition(point: ExpPoint, deliveredAt: number): ExpPoint {
    const anchor = this.latestExactEndpoint
    const transition = this.pendingLevelTransition
    if (!anchor
      || !transition
      || transition.from !== anchor.level
      || transition.to !== anchor.level + 1
      || point.level !== anchor.level
      || point.absolute >= anchor.absolute
      || point.absolute !== 0
      || point.ratio !== 0
      || Math.abs(transition.confirmedAt - point.sourceAt) > EXP_EVIDENCE_WINDOW_MS
      || deliveredAt - point.sourceAt > EXP_EVIDENCE_WINDOW_MS) {
      return point
    }
    const promoted = {
      ...point,
      level: transition.to,
      requiredExp: null,
      requiredExpApproximate: false,
    }
    return this.validPoint(promoted) ? promoted : point
  }

  private expirePendingEvidence(now: number): void {
    if (this.pendingLevelTransition
      && now - this.pendingLevelTransition.confirmedAt > EXP_EVIDENCE_WINDOW_MS) {
      this.pendingLevelTransition = null
    }
    if (this.pendingWrapEvidence
      && now - this.pendingWrapEvidence.evidence.deliveredAt > EXP_EVIDENCE_WINDOW_MS) {
      this.pendingWrapEvidence = null
    }
  }

  private rebaseTimelineAt(point: TimelinePoint): void {
    this.segmentId += 1
    const retainedFrames = this.frameTimeline
      .filter((candidate) => candidate.sourceAt >= point.sourceAt)
      .map((candidate) => ({ ...candidate, segmentId: this.segmentId }))
    const reboundFrame = { sourceAt: point.sourceAt, activeAt: point.activeAt, segmentId: this.segmentId }
    this.frameTimeline = retainedFrames.some((candidate) => candidate.sourceAt === point.sourceAt)
      ? retainedFrames.map((candidate) => candidate.sourceAt === point.sourceAt ? reboundFrame : candidate)
      : [reboundFrame, ...retainedFrames]
    const retained = this.timeline
      .filter((candidate) => candidate.sourceAt >= point.sourceAt)
      .map((candidate) => ({ ...candidate, segmentId: this.segmentId }))
    const rebound = { ...point, segmentId: this.segmentId }
    this.timeline = retained.some((candidate) => candidate.sourceAt === point.sourceAt)
      ? retained.map((candidate) => candidate.sourceAt === point.sourceAt ? rebound : candidate)
      : [rebound, ...retained]
    this.segmentOpen = true
    this.lastFrameAt = this.frameTimeline.at(-1)?.sourceAt ?? point.sourceAt
    this.exactAnchor = rebound
    this.provisionalEndpoint = rebound
    this.provisionalCandidate = null
  }

  private breakContinuity(): void {
    this.segmentId += 1
    this.segmentOpen = false
    this.lastFrameAt = null
    this.frameTimeline = []
    this.timeline = []
    this.exactAnchor = null
    this.provisionalCandidate = null
    this.provisionalEndpoint = null
    this.pendingLevelTransition = null
    this.pendingWrapEvidence = null
  }

  private openSegment(): void {
    this.segmentOpen = true
    this.lastFrameAt = null
  }

  private pruneTimeline(now: number): void {
    const cutoff = now - TIMELINE_RETENTION_MS
    this.frameTimeline = this.frameTimeline.filter((point) => point.sourceAt >= cutoff)
    this.timeline = this.timeline.filter((point) => point.sourceAt >= cutoff || point === this.exactAnchor)
  }

  private pointFromState(state: GameStateSnapshot | null): ExpPoint | null {
    if (!state || state.level === null || state.exp.absolute === null || state.exp.ratio === null || state.exp.estimated) return null
    const point: ExpPoint = {
      level: state.level,
      absolute: state.exp.absolute,
      ratio: state.exp.ratio,
      requiredExp: state.exp.requiredExp ?? null,
      percentagePrecision: state.exp.percentagePrecision,
      requiredExpApproximate: state.exp.requiredExpApproximate ?? state.exp.requiredExp !== undefined,
      sourceAt: state.observedAt?.exp ?? state.capturedAt,
    }
    return this.validPoint(point) ? point : null
  }

  private evidenceFromState(state: GameStateSnapshot): ExpExactEvidence | undefined {
    const point = this.pointFromState(state)
    if (!point) return undefined
    return {
      value: {
        absolute: point.absolute,
        ratio: point.ratio,
        requiredExp: point.requiredExp,
        requiredExpApproximate: point.requiredExpApproximate,
      },
      level: point.level,
      confidence: state.confidence.exp,
      sourceAt: point.sourceAt,
      deliveredAt: state.capturedAt,
      confirmation: 'consensus',
    }
  }

  private pointFromEvidence(evidence: ExpExactEvidence): ExpPoint | null {
    const value = evidence.value
    if (evidence.level === null || value.absolute === null || value.ratio === null || value.estimated) return null
    const point: ExpPoint = {
      level: evidence.level,
      absolute: value.absolute,
      ratio: value.ratio,
      requiredExp: value.requiredExp ?? null,
      percentagePrecision: evidence.percentagePrecision ?? value.percentagePrecision,
      requiredExpApproximate: value.requiredExpApproximate ?? value.requiredExp !== undefined,
      sourceAt: evidence.sourceAt,
    }
    return this.validPoint(point) ? point : null
  }

  private validPoint(point: ExpPoint): boolean {
    if (!Number.isInteger(point.level)
      || point.level < 1
      || !Number.isSafeInteger(point.absolute)
      || point.absolute < 0
      || !Number.isFinite(point.ratio)
      || point.ratio < 0
      || point.ratio > 1) {
      return false
    }
    return expMatchesRequirement({
      absolute: point.absolute,
      ratio: point.ratio,
      requiredExp: point.requiredExp,
      percentagePrecision: point.percentagePrecision,
    }, point.requiredExp)
  }

  private rawPointFromEvidence(evidence: ExpExactEvidence): ExpPoint | null {
    const value = evidence.value
    if (evidence.level === null
      || value.absolute === null
      || value.ratio === null
      || value.estimated
      || !Number.isInteger(evidence.level)
      || !Number.isSafeInteger(value.absolute)
      || value.absolute < 0
      || !Number.isFinite(value.ratio)
      || value.ratio < 0
      || value.ratio > 1) {
      return null
    }
    const inferred = inferExpRequirement(value)
    return {
      level: evidence.level,
      absolute: value.absolute,
      ratio: value.ratio,
      requiredExp: value.requiredExp ?? (inferred === null ? null : Math.round(inferred)),
      requiredExpApproximate: value.requiredExpApproximate ?? inferred !== null,
      sourceAt: evidence.sourceAt,
    }
  }

  private expDifference(start: ExpPoint, end: ExpPoint): number | null {
    if (end.level < start.level) return null
    if (end.level === start.level) return end.absolute - start.absolute
    if (end.level !== start.level + 1 || (end.absolute >= start.absolute && end.ratio >= start.ratio - .0002)) return null

    const startRequired = start.requiredExp ?? this.requirements.get(start.level)?.requiredExp ?? null
    if (startRequired === null || startRequired <= 0 || start.absolute > startRequired) return null
    let total = startRequired - start.absolute
    for (let level = start.level + 1; level < end.level; level += 1) {
      const required = this.requirements.get(level)?.requiredExp ?? null
      if (required === null || required <= 0) return null
      total += required
    }
    return total + end.absolute
  }

  private exactUpdate(
    continuity: AccountingContinuity,
    start: TimelinePoint | null,
    end: TimelinePoint,
    delta: number | null,
    trustedIntervalMs: number,
  ): AccountingExpUpdate {
    return {
      continuity,
      sourceAt: end.sourceAt,
      sourceStartAt: start?.sourceAt ?? null,
      sourceEndAt: end.sourceAt,
      activeStartAt: start?.activeAt ?? null,
      activeEndAt: end.activeAt,
      trustedIntervalMs,
      delta,
      exact: true,
      start: start ? this.accountingPoint(start) : null,
      end: this.accountingPoint(end),
    }
  }

  private emptyUpdate(continuity: AccountingContinuity, sourceAt: number | null = null): AccountingExpUpdate {
    return {
      continuity,
      sourceAt,
      sourceStartAt: null,
      sourceEndAt: null,
      activeStartAt: null,
      activeEndAt: null,
      trustedIntervalMs: 0,
      delta: null,
      exact: false,
      start: null,
      end: null,
    }
  }

  private accountingPoint(point: ExpPoint): AccountingExpPoint {
    return {
      admittedTotal: point.admittedTotal,
      admissionEpoch: point.admissionEpoch,
      level: point.level,
      absolute: point.absolute,
      ratio: point.ratio,
      requiredExp: point.requiredExp,
      requiredExpApproximate: point.requiredExpApproximate,
    }
  }

  private rememberRequirement(point: ExpPoint): void {
    if (point.requiredExp === null || point.requiredExp <= 0) return
    this.requirements.set(point.level, {
      requiredExp: point.requiredExp,
      approximate: point.requiredExpApproximate,
    })
  }

  private rate(total: number, durationMs: number): number {
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : 0
  }
}
